import { RadixTooltip } from '@timelyapp/react';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import styles from './AgentSessionDetails.module.scss';
import {
  type AgentPeriod,
  dailyAgentActivity,
  formatCost,
  formatDuration,
  formatTokens,
} from './agentTracking.model';

const METRICS = {
  seconds: {
    label: 'Time',
    description: 'agent time',
    hint: 'Recorded days · agent time',
    format: formatDuration,
  },
  tokens: {
    label: 'Tokens',
    description: 'tokens',
    hint: 'Includes cache reads and writes · recorded days',
    format: formatTokens,
  },
  cost: {
    label: 'Cost',
    description: 'estimated cost',
    hint: 'USD · API estimate',
    format: formatCost,
  },
};

export default function AgentSessionActivity({ periods }: { periods: AgentPeriod[] }) {
  const [metric, setMetric] = useState<keyof typeof METRICS>('seconds');
  const selectedMetric = METRICS[metric];
  const days = dailyAgentActivity(periods, metric);
  const maxValue = Math.max(0, ...days.map(day => day.value)) || 1;
  return (
    <figure
      className={styles.activityWidget}
      aria-label="Daily session activity"
      data-metric={metric}
    >
      <figcaption>
        <strong>Daily activity</strong>
        <span>{selectedMetric.hint}</span>
      </figcaption>
      <div className={styles.activityMetricPicker} role="group" aria-label="Daily activity metric">
        {(Object.keys(METRICS) as (keyof typeof METRICS)[]).map(key => (
          <button
            key={key}
            type="button"
            aria-pressed={metric === key}
            onClick={() => setMetric(key)}
          >
            {METRICS[key].label}
          </button>
        ))}
      </div>
      {!days.length ? (
        <p>No recorded activity</p>
      ) : (
        <div className={styles.activityBars}>
          {days.map(day => {
            const date = DateTime.fromISO(day.day);
            const value = day.available ? selectedMetric.format(day.value) : '—';
            const detail = `${date.toFormat('cccc, LLLL d, yyyy')}: ${day.available ? `${value}${day.partial ? ` known ${selectedMetric.description} (partial)` : ` ${selectedMetric.description}`}` : `${selectedMetric.description} unavailable`} · ${day.periods} ${day.periods === 1 ? 'period' : 'periods'}`;
            return (
              <RadixTooltip key={day.day} tooltip={detail} side="top">
                <div className={styles.activityDay} role="img" aria-label={detail} tabIndex={0}>
                  <span className={styles.activityValue}>
                    {value}
                    {day.partial ? ' +' : ''}
                  </span>
                  <span className={styles.activityTrack} aria-hidden="true">
                    <span
                      className={styles.activityBar}
                      data-unknown={!day.available || undefined}
                      data-partial={day.partial || undefined}
                      style={
                        {
                          '--day-activity-height': `${(day.value / maxValue) * 100}%`,
                        } as React.CSSProperties
                      }
                    />
                  </span>
                  <span className={styles.activityDate}>{date.toFormat('LLL d')}</span>
                </div>
              </RadixTooltip>
            );
          })}
        </div>
      )}
    </figure>
  );
}
